/* eslint-disable @typescript-eslint/no-unsafe-function-type */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import type { IFilter } from '@chax-at/prisma-filter-common';
import { ResponseType } from 'axios';
import {
  OpenAPIClientAxios,
  type AxiosError,
  type AxiosResponse,
  type Document as OpenAPIClientAxiosDocument,
  type ParamsArray,
  type SingleParam,
  type UnknownOperationMethod,
} from 'openapi-client-axios';
import { ApiError } from './api.error';
import { createFilterParams } from './api.helper';
import { FetchError } from './fetch.error';
import type { Client, OperationMethods } from './generated/api.d';
import { ErrorTypes } from './generated/api.enum';
import apiJson from './generated/api.json';

class Emitter<T> {
  private subscribers: EmitterCallback<T>[] = [];

  public subscribe(cb: EmitterCallback<T>): () => void {
    this.subscribers.push(cb);

    return () => {
      const index = this.subscribers.findIndex((s) => s === cb);
      if (index !== -1) {
        this.subscribers.splice(index, 1);
      }
    };
  }

  public emit(...args: EmitterCallbackParams<T>): void {
    this.subscribers.forEach((s) => s(...args));
  }
}

export class Api {
  private static client = this.createClient();
  private static errorEmitter = new Emitter<ApiError>();

  // special handling for downloading streams
  private static readonly RESPONSE_TYPES: Partial<Record<TOperationName, ResponseType>> = {
    SchoolSubjectController_export: 'arraybuffer',
    BillingController_downloadTenantOfferPdf: 'arraybuffer',
  };

  private constructor() {
    /** static class */
  }

  public static async request<T extends TOperationName>(
    operationName: T,
    ...[parameters, body]: TRequestArguments<T>
  ): Promise<TRequestReturnValue<T>> {
    let response: AxiosResponse | undefined;
    try {
      const { $filter, ...otherParameters } = parameters ?? {};
      response = await (this.client[operationName] as UnknownOperationMethod)(
        {
          ...createFilterParams($filter),
          ...otherParameters,
        },
        Api.transformBodyForRequest(body),
        {
          responseType: Api.RESPONSE_TYPES[operationName] || 'json',
        },
      );
    } catch (e) {
      await this.handleFetchError(e as AxiosError);
    }

    return response?.data ?? undefined;
  }

  public static Routes = this.createOperationMap();

  public static setHeader(header: string, value: string | null): void {
    this.client.defaults.headers.common[header] = value;
  }

  /**
   * Creates an API Client for the API from its openapi file
   * See backend script `tools:apidoc:clientgen`
   */
  private static createClient(): Client {
    const generatedOpenApi = new OpenAPIClientAxios({
      axiosConfigDefaults: {
        baseURL: '/api',
        withCredentials: true,
      },
      definition: apiJson as unknown as OpenAPIClientAxiosDocument,
    });

    return generatedOpenApi.initSync<Client>();
  }

  /**
   * Handles backend API errors by throwing according errors
   */
  private static async handleFetchError(err: AxiosError): Promise<never> {
    if (err.response) {
      if (err.response.data instanceof Blob) {
        err.response.data = JSON.parse(await err.response.data.text()) as unknown;
      }
      const apiError = new ApiError(err.response.data, err.config, err.request);
      Api.errorEmitter.emit(apiError);

      throw apiError;
    } else {
      throw new FetchError(ErrorTypes.UNKNOWN, err.config);
    }
  }

  public static onError(cb: (error: ApiError) => void): () => void {
    return Api.errorEmitter.subscribe(cb);
  }

  /**
   * Transforms the body if necessary.
   * Current cases:
   *  - Contains a Blob? transform into FormData
   *  - default: return body as is
   */
  private static transformBodyForRequest<T extends TOperationName>(body: TRequestBody<T>): TRequestBody<T> | FormData {
    const keys = body && Object.keys(body);
    if (keys?.some((k) => body[k] instanceof Blob)) {
      const formData = new FormData();
      for (const key of keys) {
        formData.append(key, body[key]);
      }
      return formData;
    }

    return body;
  }

  private static createOperationMap(): TOperationMap {
    const originalOperations = Object.entries(this.client).filter(
      ([operationName, value]) => typeof value === 'function' && /^.*Controller_.*/.test(operationName),
    );
    const out: Record<string, Record<string, Function>> = {};
    for (const [operationName] of originalOperations) {
      const [, controllerName, methodName] = /^(.*?)(?:Controller)?_(.*?)$/i.exec(operationName) ?? [null, null, null];
      if (!controllerName || !methodName) continue;
      const controller = (out[controllerName] = out[controllerName] || {});
      controller[methodName] = Api.request.bind(Api, operationName as TOperationName);
    }

    return out as TOperationMap;
  }
}

export type EmitterCallback<T> = (...args: EmitterCallbackParams<T>) => void;
type EmitterCallbackParams<T> = void extends T ? [] : [data: T];

type TOperationName = keyof OperationMethods;
type TUnpromise<T extends Promise<unknown>> = T extends Promise<infer TResult> ? TResult : never;

type TRequestParameters<T extends TOperationName> = Exclude<
  Required<Parameters<OperationMethods[T]>>[0],
  ParamsArray | SingleParam | null
>;
type TRequestBody<T extends TOperationName> = Required<Parameters<OperationMethods[T]>>[1];

type TRequestHasBody<T extends TOperationName> = unknown extends TRequestBody<T> ? false : true;
type TRequestHasParameters<T extends TOperationName> = keyof TRequestParameters<T> extends string ? true : false;
type TRequestFilter = { $filter?: IFilter };

type TRequestArguments<T extends TOperationName> =
  TRequestHasBody<T> extends true
    ? [
        parameters: TRequestHasParameters<T> extends true
          ? TRequestParameters<T> & TRequestFilter
          : null | undefined | TRequestFilter,
        body: TRequestBody<T>,
      ]
    : TRequestHasParameters<T> extends true
      ? [parameters: TRequestParameters<T> & TRequestFilter]
      : [parameters?: TRequestFilter];

type TRequestReturnValue<T extends TOperationName> = TUnpromise<ReturnType<OperationMethods[T]>>['data'];

// Types to get an operation map by Controller and Method
type TOperationUnion<T extends TOperationName = TOperationName> = T extends `${infer TC}Controller_${infer TM}` &
  TOperationName
  ? {
      [PC in TC]: {
        [PM in TM]: (...[parameters, body]: TRequestArguments<T>) => Promise<TRequestReturnValue<T>>;
      };
    }
  : never;

type TUnionToIntersection<U> = (U extends any ? (x: U) => void : never) extends (x: infer I) => void ? I : never;

type TOperationMap = TUnionToIntersection<TOperationUnion>;
